import React, { useEffect, useRef, useState } from 'react'
// import { useSearchParams, ScrollRestoration } from 'react-router-dom'

import styled, { css } from 'styled-components'
import DetailedBoardRow from '../../components/DetailedBoardRow'
import { StyledDetailedBoardHeaders } from '../../public/PublicDetailedBoardPage'

import { sortPlayersByChapterRanks } from '../../utils'

import FlipMove from 'react-flip-move'

const DESKTOP_SCALE_BY = 0.9
const PHONE_SCALE_BY = 0.7

const SCENES_WITH_PLAYER_SELECTION = ['4_view_simple', '5_view', '1_view', '2_view']
const SCENES_WITH_PAGE_VALUES = ['4_view_detailed', 'summary', '4_view_simple', '5_view', '1_view']
const SCENES_WITH_LIVE_PLAYERS = ['4_view_simple', '4_view_detailed', '5_view', '1_view', '2_view']

import { useStore } from '../../zustandStore'
import { shallow } from 'zustand/shallow'

const WithDetailedBoardLayout = ({
  pushPlayerToBroadcast,
  children,
  setSortingByChapterName = () => {},
  sortingByChapterName,
}) => {

  const {
    players,
    event,
    obsState,

    isLoading,

    stagedObsState,
    setStagedObsState,
    setStagedPlayerSlug,
    unstageObsState,
  } = useStore(
    state => ({
      players:      state.players,
      event:        state.event,
      obsState: state.obsState,

      isLoading: state.isLoading,

      stagedObsState:     state.stagedObsState,
      setStagedObsState:  state.setStagedObsState,
      setStagedPlayerSlug: state.setStagedPlayerSlug,
      unstageObsState:    state.unstageObsState,
    }),
    shallow
  )

  const isPlayerSelectionAvailableForScene   = () => SCENES_WITH_PLAYER_SELECTION.includes(obsState.currentScene)
  const isPageSelectionAvailableForScene     = () => SCENES_WITH_PAGE_VALUES     .includes(obsState.currentScene)
  const isLivePlayersFilterAvailableForScene = () => SCENES_WITH_LIVE_PLAYERS    .includes(obsState.currentScene)

  const broadcastPlayerSlug = obsState.currentPlayerSlug

  const stagedPlayerSlug = stagedObsState.currentSourceType == 'player' && stagedObsState.currentPlayerSlug

  let sortedPlayers = players

  if (sortingByChapterName && event.chapterNames.includes(sortingByChapterName)) {
    sortedPlayers = sortPlayersByChapterRanks(players, sortingByChapterName)
  }

  const [areLivePlayersShown, setLivePlayersShown] = useState(true)

  useEffect(() => {
    setLivePlayersShown(SCENES_WITH_LIVE_PLAYERS.includes(obsState.currentScene))
  }, [obsState.currentScene])

  const onClickColumnHeader = (clickedChapterName) => (e) => {
    e.preventDefault()
    if (sortingByChapterName != clickedChapterName) {
      setSortingByChapterName(clickedChapterName)
    } else {
      setSortingByChapterName(null)
    }
  }

  const onClickRevertSorting = (e) => {
    e.preventDefault()

    setSortingByChapterName(null)
  }

  const pageWrapperRef = useRef()

  const livePlayers = sortedPlayers.filter(p => obsState.livePlayerSlugs.includes(p.slug))

  const playersList = (displayedPlayers) => (
    displayedPlayers.map((player, i) => {
      let className = 'row'
      className += (player.slug === broadcastPlayerSlug ? ' row-broadcast' : '')
      className += (player.slug === stagedPlayerSlug ? ' row-staged' : '')
      className += (i % 2 === 0 ? ' row-even' : ' row-odd')
      const onDoubleClick = isPlayerSelectionAvailableForScene() && pushPlayerToBroadcast
        ? e => {
          e.preventDefault()
          pushPlayerToBroadcast(player.slug)
        } : null

      const onClick = isPlayerSelectionAvailableForScene() && setStagedPlayerSlug && pushPlayerToBroadcast && player.slug !== stagedPlayerSlug
        ? (e) => {
          e.preventDefault()
          setStagedPlayerSlug(player.slug)
        } : null

      let clickHandlerProps = {}

      if (player.slug !== stagedPlayerSlug) {
        if (onClick != null) {
          clickHandlerProps = { onClick: onClick }
        }
      } else {
        if (onDoubleClick != null) {
          clickHandlerProps = { onDoubleClick: onDoubleClick }
        }
      }

      return (
        <DetailedBoardRow
          className={className}
          key={player.slug}
          chapterNames={event.chapterNames}
          player={player}
          includeTotal
          isLive={obsState.livePlayerSlugs.includes(player.slug)}
          includeRankForChapters={[sortingByChapterName]}
          scaleBy={DESKTOP_SCALE_BY}
          scaleByOnPhone={PHONE_SCALE_BY}
          isFeatured={player.slug === obsState.featuredPlayerSlug}
          isLoading={isPlayerSelectionAvailableForScene() && isLoading}
          isDisabled={!isPlayerSelectionAvailableForScene()}
          loadingDiv={<LoadingRowDiv />}
          {...clickHandlerProps}
        />
      )
    })
  )

  // TODO: handle the cases of 1 focused player, since that was part of the point of passing the child components in before
  return (
    <PageWrapper ref={pageWrapperRef} isLoading={isLoading}>

      <PositionStickyWrapper>
        {isLoading &&
          <GridCell className='loading-div'>
            <LoadingDiv>
              <LoadingSpinnerLogo src={'/images/logos/ctt.png'} />
            </LoadingDiv>
          </GridCell>
        }

        <GridCell>{children}</GridCell>

        <StyledTable>
          {SCENES_WITH_LIVE_PLAYERS.includes(obsState.currentScene) && areLivePlayersShown && (<>
            <ThinHeaders
              key='live-header'
              className='header-row thin'
              chapterNames={event.chapterNames}
              includeTotal
              scaleBy={DESKTOP_SCALE_BY}
              scaleByOnPhone={PHONE_SCALE_BY}
              sortingByChapterName={sortingByChapterName}
              onClickColumnHeader={onClickColumnHeader}
              onClickRevertSorting={onClickRevertSorting}
              onClickNameColumn={() => setLivePlayersShown(!areLivePlayersShown)}
              totalSelected={sortingByChapterName == null}
            >
              Hide Live Players
            </ThinHeaders>
            {areLivePlayersShown && (
              <StyledPlayersTable disableClickPlayers={!isPlayerSelectionAvailableForScene() || isLoading}>
                <FlipMove
                  enterAnimation="accordionVertical"
                  leaveAnimation="accordionVertical"
                  className='gradient-background'
                  style={{ width: '100%' }}
                >
                  {playersList(livePlayers)}
                </FlipMove>
              </StyledPlayersTable>
            )}
          </>)}
          <ThinHeaders
            key='all-header'
            className='header-row thin'
            chapterNames={event.chapterNames}
            includeTotal
            scaleBy={DESKTOP_SCALE_BY}
            scaleByOnPhone={PHONE_SCALE_BY}
            sortingByChapterName={sortingByChapterName}
            onClickColumnHeader={onClickColumnHeader}
            onClickNameColumn={SCENES_WITH_LIVE_PLAYERS.includes(obsState.currentScene) ? () => setLivePlayersShown(!areLivePlayersShown) : null}
            totalSelected={sortingByChapterName == null}
          >
            {areLivePlayersShown
              ? 'All Players'
              : SCENES_WITH_LIVE_PLAYERS.includes(obsState.currentScene) 
                ? 'Show Live Players'
                : 'All Players'
            }
          </ThinHeaders>
        </StyledTable>

      </PositionStickyWrapper>

      <StyledPlayersTable disableClickPlayers={!isPlayerSelectionAvailableForScene() || isLoading}>
        <FlipMove
          enterAnimation="accordionVertical"
          leaveAnimation="accordionVertical"
          className='gradient-background'
          style={{ width: '100%' }}
        >
          {playersList(sortedPlayers)}
        </FlipMove>
      </StyledPlayersTable>
    </PageWrapper>
  )
}

export default WithDetailedBoardLayout

const DOTTED_BORDER_WIDTH = '2px';

const PageWrapper = styled.div`
  margin-bottom: 2em;
`
const GridCell = styled.div`
  margin: 0 auto;
  grid-column: 1;
  grid-row: 1;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  width: 100%;
  &.loading-div {
    height: 100%;
  }
`

const LoadingDiv = styled.div`
  cursor: wait!important;
  background-color: var(--loading-div-bg-color);
  z-index: 10;
  display: grid;

  height: 100%;
  width: min(var(--detailed-board-width), 98%);
  margin: 0 auto;
`

const LoadingSpinnerLogo = styled.img`
  user-select: none;
  --width: ${props => props.width || '150px'};
  opacity: 0.7;
  align-self: center;
  justify-self: center;
  width: var(--width);
  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
  transform-origin: calc(var(--width) * 0.48) calc(var(--width) * 0.58);
  animation: spin 1.5s linear infinite;
`

const LoadingRowDiv = styled.div`
  cursor: wait!important;
  background-color: var(--loading-div-bg-color);
  z-index: 10;
  top: -3px;
  left: -3px;
  position: absolute;
`

const StyledTable = styled.div`
  &.pinned {
    background: linear-gradient(207deg, #33152d, #141732);
  }

  margin: 0 auto;
  display: grid;
  @media only screen and (min-width: ${() => `${window.phoneCutoffSizePx}px`}) {

    .header-row {
      font-size: 34.67px;
    }
  }
  @media only screen and (max-width: ${() => `${window.phoneCutoffSizePx}px`}) {
    .row {
      font-size: 2.3vw;
    }
    .header-row {
      font-size: 2.5vw;
      border-top-width: 1.5px;
      border-bottom-width: 1.5px;
    }
  }

  .chapter-rank {
    font-size: 65%;
  }

  flex-flow: column nowrap;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  width: calc(100% + ${() => DOTTED_BORDER_WIDTH} * 2);
  max-width: var(--detailed-board-width);

  .row {
    width: calc(100% - ${() => DOTTED_BORDER_WIDTH}*2);
  }

  .row-odd {
    background-color: #0000;
    border: ${() => DOTTED_BORDER_WIDTH} solid #0000;
  }
  .row-even {
    background-color: var(--every-other-row-color);
    border: ${() => DOTTED_BORDER_WIDTH} solid #0000;
  }

  .row-broadcast {
    background-color: var(--row-selected-color)!important;
    cursor: default!important;
  }

  .row-staged, .row-staged:hover, .row-staged:active {
    border: ${() => DOTTED_BORDER_WIDTH} dashed var(--green-text)!important;
  }
`

const StyledPlayersTable = styled(StyledTable)`
  --row-hover-color: var(--green-hover);
  --row-active-color: var(--green-active);
  --row-selected-color: var(--green-selected);
  --row-selected-color: var(--green-selected);
  border-bottom: 2px solid #aaa;

  position: relative;
  user-select: none;
  .row { cursor: pointer; }
  ${props => props.disableClickPlayers && css`
    ${'' /* darker hover color here because you can't actually click these rows */}
    --row-hover-color: var(--row-greyed-out);
    --row-active-color: var(--row-greyed-out);
    > * {
      cursor: default!important;
    }
    .row { cursor: default; }
    .row:hover, .row:active {
      border: ${() => DOTTED_BORDER_WIDTH} solid var(--row-greyed-out);
    }
  `}
`

const ThinHeaders = styled(StyledDetailedBoardHeaders)`
  width: 100%;
  &.thin {
    font-size: 1.4em;
    .column-player-children {
      font-size: 1em;
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 1!important;
    }
  }
`

const PositionStickyWrapper = styled.div`
  position: sticky;
  top: 0px;
  z-index: 5;
  display: grid;
`
